import React from 'react';
import { Link } from 'react-router-dom';
import { FaBook, FaUser, FaShoppingCart, FaList, FaUsers } from 'react-icons/fa';

const Home = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-neutral-100">
      {/* Hero Section */}
      <section className="px-4 pt-24 pb-16 mx-auto text-center max-w-7xl sm:px-6 lg:px-8">
        <h1 className="mt-12 mb-6 text-5xl font-extrabold text-blue-600 md:text-6xl animate-fade-in">
          Welcome to Bookstore API
        </h1>
        <p className="max-w-3xl mx-auto mb-8 text-xl text-gray-700 delay-100 animate-fade-in">
          Manage your bookstore with ease using our powerful API. Create, update, and explore books, authors, customers, carts, and orders seamlessly.
        </p>
        <Link
          to="/books"
          className="inline-block px-8 py-4 text-lg font-semibold text-white transition-all delay-200 transform rounded-lg shadow-lg bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 hover:scale-105 animate-fade-in"
          aria-label="Get Started with Books"
        >
          Start Exploring
        </Link>
        {/* How It Works Card */}
        <div className="max-w-3xl p-8 mx-auto mt-12 delay-300 bg-white border-l-4 border-blue-600 shadow-xl rounded-2xl animate-fade-in">
          <h2 className="mb-4 text-2xl font-bold text-blue-600">How It Works</h2>
          <p className="mb-6 text-gray-700">
            Get started with the Bookstore API in just a few simple steps. Follow the guide below to manage your bookstore resources efficiently.
          </p>
          <ol className="space-y-3 text-left text-gray-700 list-decimal list-inside">
            <li>
              <span className="font-semibold">Navigate to a Resource:</span> Use the navbar or the "Explore Resources" cards below to visit sections like Books, Authors, Customers, Cart, or Orders.
            </li>
            <li>
              <span className="font-semibold">Use Sample Data:</span> Each form includes a "Sample Data" section with example JSON. Toggle it open to copy values for testing API actions.
            </li>
            <li>
              <span className="font-semibold">Execute Actions:</span> Fill in the form fields (using sample data or your own) and click buttons like Create, Get, Update, or Delete to interact with the API.
            </li>
            <li>
              <span className="font-semibold">Manage Carts & Orders:</span> Add books to your cart with a stunning modal experience, then create orders to finalize purchases.
            </li>
          </ol>
        </div>
      </section>

      {/* Features Section */}
      <section className="px-4 py-16 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <h2 className="mb-12 text-3xl font-bold text-center text-blue-600">Why Choose Bookstore API?</h2>
        <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
          {[
            {
              icon: <FaBook className="text-4xl text-blue-600" />,
              title: 'Manage Books',
              description: 'Add, update, or delete books with detailed attributes like ISBN, price, and stock.',
            },
            {
              icon: <FaShoppingCart className="text-4xl text-orange-500" />,
              title: 'Stunning Cart',
              description: 'Enjoy a modern cart experience with animations and real-time updates.',
            },
            {
              icon: <FaList className="text-4xl text-green-500" />,
              title: 'Track Orders',
              description: 'Create and view orders effortlessly, linked to your cart items.',
            },
          ].map((feature, index) => (
            <div
              key={feature.title}
              className="p-6 transition-all transform bg-white shadow-xl rounded-2xl hover:shadow-2xl hover:-translate-y-1 animate-fade-in"
              style={{ animationDelay: `${300 + index * 100}ms` }}
            >
              <div className="flex justify-center mb-4">{feature.icon}</div>
              <h3 className="mb-2 text-xl font-semibold text-gray-800">{feature.title}</h3>
              <p className="text-gray-600">{feature.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Navigation Section */}
      <section className="px-4 py-16 m-8 mx-auto bg-white shadow-lg max-w-7xl sm:px-6 lg:px-8 rounded-2xl">
        <h2 className="mb-12 text-3xl font-bold text-center text-blue-600">Explore Resources</h2>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3">
          {[
            { to: '/books', label: 'Books', icon: <FaBook />, color: 'bg-blue-600 hover:bg-blue-700' },
            { to: '/authors', label: 'Authors', icon: <FaUser />, color: 'bg-green-500 hover:bg-green-600' },
            { to: '/customers', label: 'Customers', icon: <FaUsers />, color: 'bg-yellow-500 hover:bg-yellow-600' },
            { to: '/cart', label: 'Cart', icon: <FaShoppingCart />, color: 'bg-orange-500 hover:bg-orange-600' },
            { to: '/orders', label: 'Orders', icon: <FaList />, color: 'bg-purple-500 hover:bg-purple-600' },
          ].map((item, index) => (
            <Link
              key={item.to}
              to={item.to}
              className={`${item.color} p-6 text-white rounded-xl text-center font-semibold text-lg flex items-center justify-center space-x-2 transition-all transform hover:scale-105 shadow-md animate-fade-in`}
              style={{ animationDelay: `${500 + index * 100}ms` }}
              aria-label={`Navigate to ${item.label}`}
            >
              {item.icon}
              <span>{item.label}</span>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Home;